/**
 * Alerts (2.5): the patient's slice of the one branch worklist, promoted to the
 * top of the patient page and drawn as a strip rather than a table — one row per
 * item, the kind and the countdown, with the reason underneath.
 *
 * The rows come from `selectWorklistRows` under the patient filter, so they are
 * the same rows, in the same order, as the Compliance tab shows when it is
 * narrowed to this patient. Nothing is added here that is not already a worklist
 * row, and nothing is assigned: no claim, no resolve, no escalation. A row leaves
 * when its cause clears — and when the last one goes **the panel goes with it**
 * (Pete, 2026-09-21). A section headed Alerts saying there are none is a thing
 * to read and dismiss on every patient who is fine, which is most of them; the
 * absence of the section is the same information and costs nothing.
 */
import { useMemo } from "react";
import { Box, Chip, Stack, Typography } from "@mui/material";
import { useBranchClock } from "../../components/branchClock";
import { useNow } from "../../components/useNow";
import { KIND_LABELS, selectWorklistRows } from "../../engine/worklist";
import { useDemoState } from "../../store/useDemoStore";
import { SeverityDot } from "./WorklistRows";

/** The patient's alert rows. The section above uses it to decide whether to draw a card at all. */
export function usePatientAlertRows(patientRef: string) {
  const state = useDemoState();
  const now = useNow();
  return useMemo(() => selectWorklistRows(state, now, { patientRef }), [state, now, patientRef]);
}

export function PatientAlertsPanel({ patientRef }: { patientRef: string }) {
  const state = useDemoState();
  const clock = useBranchClock(state.config.branch.timeZone);
  const rows = usePatientAlertRows(patientRef);
  if (rows.length === 0) return null;

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="baseline" sx={{ mb: 1 }}>
        <Typography variant="h6">Alerts</Typography>
        <Typography variant="caption" color="text.secondary">
          {`${rows.length} item${rows.length === 1 ? "" : "s"}`}
        </Typography>
      </Stack>

      <Stack spacing={0.75}>
          {rows.map((row) => (
            <Stack
              key={row.id}
              direction="row"
              spacing={1}
              alignItems="baseline"
              flexWrap="wrap"
              useFlexGap
              data-alert-row={row.id}
              data-kind={row.kind}
              sx={{ border: "1px solid #e6e6e6", borderLeft: `4px solid ${row.severity === 3 ? "#c62828" : row.severity === 2 ? "#ef6c00" : "#9e9e9e"}`, borderRadius: 1, px: 1, py: 0.5 }}
            >
              <SeverityDot severity={row.severity} />
              <Chip size="small" variant="outlined" label={KIND_LABELS[row.kind]} />
              {row.discipline && <Chip size="small" label={row.discipline} sx={{ height: 18, fontSize: 10.5 }} />}
              <Typography variant="body2" sx={{ flex: "1 1 320px" }}>
                {clock.sentence(row.reason)}
              </Typography>
              {row.countdown && <Countdown text={clock.sentence(row.countdown)} />}
            </Stack>
        ))}
      </Stack>
    </Box>
  );
}

/** The countdown, red once it has run out. "overdue" is the engine's own word, read off the phrased line. */
function Countdown({ text }: { text: string }) {
  return (
    <Typography variant="caption" sx={{ whiteSpace: "nowrap", color: text.includes("overdue") ? "error.main" : "text.secondary" }}>
      {text}
    </Typography>
  );
}
